import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WELCOME_MODAL_SESSION_KEY = "614_logged_in_welcome_seen";

const LoggedInWelcomeModal = () => {
  const { isAuthenticated, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [popupContent, setPopupContent] = useState(null);

  useEffect(() => {
    if (loading || !isAuthenticated) return undefined;
    if (sessionStorage.getItem(WELCOME_MODAL_SESSION_KEY) === "true") return undefined;

    let mounted = true;
    let timer;

    axios.get(`${API}/site-content`)
      .then((response) => {
        if (!mounted) return;
        const loginPopup = response.data.login_popup;
        setPopupContent(loginPopup);
        if (loginPopup?.enabled) {
          timer = window.setTimeout(() => {
            setOpen(true);
            sessionStorage.setItem(WELCOME_MODAL_SESSION_KEY, "true");
          }, 3500);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [isAuthenticated, loading]);

  if (loading || !isAuthenticated || !popupContent?.enabled) return null;

  const contactEmail = popupContent.email?.trim();
  const discordUrl = popupContent.discord_url?.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg rounded-2xl border border-gray-100 p-0 overflow-hidden">
        <div className="bg-[#1a1a1a] px-6 py-7 text-white">
          <div className="w-12 h-12 rounded-full bg-[#42D78A]/10 flex items-center justify-center mb-4">
            <Heart className="w-6 h-6 text-[#42D78A]" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {popupContent.title}
            </DialogTitle>
            <DialogDescription className="text-gray-300 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {popupContent.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="px-6 py-6">
          <p className="text-sm text-gray-600 leading-relaxed mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {popupContent.message}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            {discordUrl && (
              <a href={discordUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-2 border-[#1a1a1a] font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Discord
                </Button>
              </a>
            )}
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-2 border-[#1a1a1a] font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Email Us
                </Button>
              </a>
            )}
            <Button
              onClick={() => setOpen(false)}
              className="flex-1 bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full font-bold"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Got it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoggedInWelcomeModal;
